import { gql, useQuery } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import type {
  NexoyaContentLabelAssignment,
  NexoyaContentLabelCategory,
  NexoyaContentLabelCategoryValue,
  NexoyaContentV2,
} from '../../types';

import { CONTENT_LABEL_VALUE_REF_FRAGMENT } from './fragments';
import { STAGE_READ_CONTEXT } from './stageReads';

/**
 * Content Labels v2 — the label state of the contents ON ONE PAGE of the
 * portfolio Content tab, keyed by their ids.
 *
 * WHY A DOCUMENT OF ITS OWN, and not `labelAssignments` added to
 * `PORTFOLIO_PARENT_CONTENTS_QUERY`. That query is paged by cursor through
 * `fetchMore`, and `fetchMore` does not update the `ObservableQuery`'s own
 * variables (@apollo/client 3.13.9). So a post-assignment
 * `refetchQueries: ['PortfolioContents']` re-fetches PAGE 1, Apollo delivers it
 * as the hook's `data`, and the reader sitting on page 6 silently gets page 1's
 * rows written into their page. Here the page's ids are the field's ARGUMENTS:
 * each page occupies its own `listContentsByIds({...})` root field, so a
 * refetch always answers for the page on screen.
 *
 * `ContentV2` is deliberately NOT normalized (no `keyFields`, see
 * `fragments.ts`), so two pages' answers sit side by side rather than merging.
 *
 * Note the scalars: `listContentsByIds(contentIds: [Float!]!, teamId: Float!)` —
 * `Float!`, unlike the `Int!` team id every other root field in this folder
 * takes. The schema test next door is what holds that down.
 *
 * `labelAssignments` takes no arguments, so one content answers for every
 * category of the team: 50 rows x N categories per page. Fine at the two or
 * three categories this ships for; revisit at twenty.
 */
export const CONTENT_LABEL_ASSIGNMENTS_QUERY = gql`
  query ContentLabelAssignmentsByContentIds($teamId: Float!, $contentIds: [Float!]!) {
    listContentsByIds(teamId: $teamId, contentIds: $contentIds) {
      contentId
      title
      labelAssignments {
        labelStatus
        contentLabelCategory {
          contentLabelCategoryId
        }
        matches {
          ...ContentLabelValueRef
        }
        assignment {
          ...ContentLabelValueRef
        }
      }
    }
  }
  ${CONTENT_LABEL_VALUE_REF_FRAGMENT}
`;

/** The entity key alone — see `CONTENT_LABEL_VALUE_REF_FRAGMENT` for why nothing else. */
type ValueRef = Pick<NexoyaContentLabelCategoryValue, 'contentLabelCategoryValueId'>;

export type AssignedContent = Pick<NexoyaContentV2, 'contentId' | 'title'> & {
  labelAssignments: Array<
    Pick<NexoyaContentLabelAssignment, 'labelStatus'> & {
      contentLabelCategory: Pick<NexoyaContentLabelCategory, 'contentLabelCategoryId'>;
      matches: ValueRef[];
      assignment?: ValueRef | null;
    }
  >;
};

export type ContentLabelAssignmentsResponse = { listContentsByIds: AssignedContent[] };

export type ContentLabelAssignmentsVariables = { teamId: number; contentIds: number[] };

/**
 * `STAGE_READ_CONTEXT` (`queryDeduplication: false`) because a manual
 * assignment asks this read again the moment it succeeds
 * (`mutationEffects.activeAssignmentReads`). With deduplication on, that
 * refetch would join a request already in flight — one computed BEFORE the
 * write — and Apollo would keep its answer as the newest.
 *
 * `cache-and-network` because that refetch reaches only reads on screen: a rule
 * edited in Settings leaves this page's cached answer stale, and a remount must
 * show it and ask again, as `PortfolioContents` does for the rows.
 */
export function useContentLabelAssignmentsQuery({
  contentIds,
  skip = false,
}: {
  /** The ids of the page ON SCREEN. An empty page sends nothing. */
  contentIds: number[];
  skip?: boolean;
}) {
  const { teamId } = useTeam();

  return useQuery<ContentLabelAssignmentsResponse, ContentLabelAssignmentsVariables>(CONTENT_LABEL_ASSIGNMENTS_QUERY, {
    variables: { teamId, contentIds },
    // `contentIds: []` would read nothing and still occupy a cache field of its own.
    skip: !teamId || skip || !contentIds?.length,
    fetchPolicy: 'cache-and-network',
    context: STAGE_READ_CONTEXT,
  });
}
